import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';
import getApiUrl from '../utils/getApiUrl';
import {
    getCarrinho,
    addItemCarrinho,
    updateItemCarrinho,
    removeItemCarrinho,
    clearCarrinho,
    finalizarCarrinho,
    abandonarCarrinho,
    atualizarPassoCarrinho
} from '../services/api';

const SECRET_KEY = 'my-custom-secret-key:1af0addcce4b56f753ed10c285667ad031bae57af19747f576b05312b8cf85be';
const EXPIRATION_TIME = 60 * 60 * 1000; // 1 hora
const LOCAL_STORAGE_KEY = 'itensCarrinhoMobile';

/**
 * Hook para gerenciamento de carrinho com sync backend + localStorage
 *
 * @param {string|number} idEmpresa - ID da empresa
 * @param {object} options - Opções de configuração
 * @param {boolean} options.syncWithBackend - Se deve sincronizar com backend (default: true)
 * @param {boolean} options.trackAbandonment - Se deve rastrear abandono (default: true)
 */
const useCarrinho = (idEmpresa, options = {}) => {
    const {
        syncWithBackend = true,
        trackAbandonment = true
    } = options;

    // Estados do carrinho
    const [itensCarrinho, setItensCarrinho] = useState([]);
    const [carrinhoId, setCarrinhoId] = useState(null);
    const [sessionToken, setSessionToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [ultimoPasso, setUltimoPasso] = useState('cardapio');

    // Refs para controle
    const isInitialized = useRef(false);
    const lastSyncTime = useRef(null);

    // ===== FUNÇÕES DE CÁLCULO (mantendo compatibilidade com carrinho.jsx) =====

    /**
     * Calcula valor dos adicionais de um grupo
     */
    const calcularValorAdicionais = useCallback((gruposAdicionais) => {
        if (!gruposAdicionais || !Array.isArray(gruposAdicionais)) return 0;

        return gruposAdicionais.reduce((acc, grupo) => {
            if (grupo.calcular_maior_valor) {
                // Pegar o maior valor entre os adicionais
                const maxAdicional = grupo.adicionais.reduce((max, adicional) => {
                    return adicional.price > max ? adicional.price : max;
                }, 0);
                return acc + maxAdicional;
            } else if (grupo.calcular_media) {
                // Calcular média dos valores dos adicionais selecionados
                const adicionaisSelecionados = grupo.adicionais.filter(adicional => adicional.quantity > 0);
                if (adicionaisSelecionados.length > 0) {
                    const somaValores = adicionaisSelecionados.reduce((sum, adicional) => {
                        return sum + (adicional.price * adicional.quantity);
                    }, 0);
                    const mediaValor = somaValores / adicionaisSelecionados.length;
                    return acc + mediaValor;
                }
                return acc;
            } else {
                // Somar todos os adicionais
                return acc + grupo.adicionais.reduce((sum, adicional) => {
                    return sum + (adicional.price * adicional.quantity);
                }, 0);
            }
        }, 0);
    }, []);

    /**
     * Calcula subtotal de um item
     */
    const calcularSubtotalItem = useCallback((item) => {
        const valorItemBase = typeof item.price === 'string' && item.price.includes('U2F')
            ? parseFloat(CryptoJS.AES.decrypt(item.price, SECRET_KEY).toString(CryptoJS.enc.Utf8))
            : parseFloat(item.price) || 0;

        const valorAdicionais = calcularValorAdicionais(item.grupos_adicionais_selecionados);

        // Sempre: (base + adicionais) * quantidade
        // Adicionais são POR UNIDADE, então multiplicam pela quantidade
        return (valorItemBase + valorAdicionais) * item.quantity;
    }, [calcularValorAdicionais]);

    /**
     * Calcula valor total do carrinho
     */
    const calcularValorTotal = useCallback(() => {
        return itensCarrinho.reduce((total, item) => {
            // Se tem total encriptado, usar ele
            if (item.total && typeof item.total === 'string' && item.total.includes('U2F')) {
                const decryptedTotal = parseFloat(
                    CryptoJS.AES.decrypt(item.total, SECRET_KEY).toString(CryptoJS.enc.Utf8)
                );
                return total + decryptedTotal;
            }
            // Caso contrário, calcular
            return total + calcularSubtotalItem(item);
        }, 0);
    }, [itensCarrinho, calcularSubtotalItem]);

    /**
     * Formata valor para exibição (R$ X,XX)
     */
    const formatarValor = useCallback((valor) => {
        return valor.toFixed(2).toString().replace('.', ',');
    }, []);

    // ===== FUNÇÕES DE SINCRONIZAÇÃO =====

    /**
     * Carrega carrinho do localStorage
     */
    const loadFromLocalStorage = useCallback(() => {
        try {
            const now = Date.now();
            const itensFromStorage = localStorage.getItem(LOCAL_STORAGE_KEY);

            if (itensFromStorage) {
                const itens = JSON.parse(itensFromStorage).filter(
                    item => now - item.addedAt < EXPIRATION_TIME
                );
                // Atualizar localStorage removendo expirados
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(itens));
                return itens;
            }
        } catch (error) {
            console.error('[CARRINHO] Erro ao carregar localStorage:', error);
        }
        return [];
    }, []);

    /**
     * Salva carrinho no localStorage
     */
    const saveToLocalStorage = useCallback((itens) => {
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(itens));
        } catch (error) {
            console.error('[CARRINHO] Erro ao salvar localStorage:', error);
        }
    }, []);

    /**
     * Sincroniza com backend
     */
    const syncWithServer = useCallback(async () => {
        if (!syncWithBackend || !idEmpresa) return;

        try {
            setIsSyncing(true);
            const response = await getCarrinho(idEmpresa);

            if (response.data?.success) {
                const { carrinho, session_token } = response.data;
                setCarrinhoId(carrinho?._id);
                setSessionToken(session_token);
                lastSyncTime.current = Date.now();

                // Converter itens do backend para formato local se necessário
                if (carrinho?.itens && carrinho.itens.length > 0) {
                    const itensConvertidos = carrinho.itens.map(item => ({
                        id: item._id || item.id,
                        item_id: item.item_id,
                        title: item.title,
                        quantity: item.quantity,
                        price: item.unit_price, // Número puro
                        total: item.subtotal,   // Número puro
                        type: item.type,
                        image: item.image,
                        grupos_adicionais_selecionados: item.grupos_adicionais_selecionados || [],
                        notes: item.notes,
                        addedAt: new Date(item.added_at).getTime()
                    }));
                    setItensCarrinho(itensConvertidos);
                    saveToLocalStorage(itensConvertidos);
                }
            }
        } catch (error) {
            console.error('[CARRINHO] Erro ao sincronizar com servidor:', error);
            // Em caso de erro, usar localStorage
            const localItens = loadFromLocalStorage();
            setItensCarrinho(localItens);
        } finally {
            setIsSyncing(false);
        }
    }, [idEmpresa, syncWithBackend, loadFromLocalStorage, saveToLocalStorage]);

    // ===== INICIALIZAÇÃO =====

    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const init = async () => {
            setIsLoading(true);
            try {
                // Primeiro, carregar do localStorage para resposta imediata
                const localItens = loadFromLocalStorage();
                setItensCarrinho(localItens);

                // ✅ OTIMIZAÇÃO: Só sincronizar com backend se tem itens locais
                // Isso evita criar carrinhos vazios no servidor
                if (syncWithBackend && idEmpresa && localItens.length > 0) {
                    await syncWithServer();
                }
            } finally {
                setIsLoading(false);
            }
        };

        init();
    }, [idEmpresa, loadFromLocalStorage, syncWithBackend, syncWithServer]);

    // ===== FUNÇÕES DE MANIPULAÇÃO DO CARRINHO =====

    /**
     * Adiciona item ao carrinho
     */
    const adicionarItem = useCallback(async (item) => {
        const novoItem = {
            ...item,
            id: item.id || `${item.item_id}_${Date.now()}`,
            addedAt: Date.now()
        };

        // Calcular total se não fornecido
        if (!novoItem.total) {
            novoItem.total = calcularSubtotalItem(novoItem); // Número puro
        }

        // Atualizar estado local primeiro (optimistic update)
        const novosItens = [...itensCarrinho, novoItem];
        setItensCarrinho(novosItens);
        saveToLocalStorage(novosItens);

        // Sincronizar com backend
        if (syncWithBackend && idEmpresa) {
            try {
                const priceDecrypted = typeof item.price === 'string' && item.price.includes('U2F')
                    ? parseFloat(CryptoJS.AES.decrypt(item.price, SECRET_KEY).toString(CryptoJS.enc.Utf8))
                    : parseFloat(item.price) || 0;

                await addItemCarrinho(idEmpresa, {
                    item_id: item.item_id,
                    title: item.title,
                    quantity: item.quantity || 1,
                    unit_price: priceDecrypted,
                    type: item.type || 'normal',
                    image: item.image || '',
                    grupos_adicionais_selecionados: item.grupos_adicionais_selecionados || [],
                    notes: item.notes || ''
                });
            } catch (error) {
                console.error('[CARRINHO] Erro ao adicionar item no servidor:', error);
                // Continua com localStorage mesmo se backend falhar
            }
        }

        return novoItem;
    }, [itensCarrinho, idEmpresa, syncWithBackend, calcularSubtotalItem, saveToLocalStorage]);

    /**
     * Incrementa quantidade de um item
     */
    const incrementarQuantidade = useCallback(async (itemId, index) => {
        const novosItens = itensCarrinho.map((item, i) => {
            if (i === index || item.id === itemId) {
                const novaQuantidade = item.quantity + 1;
                const valorItemBase = typeof item.price === 'string' && item.price.includes('U2F')
                    ? parseFloat(CryptoJS.AES.decrypt(item.price, SECRET_KEY).toString(CryptoJS.enc.Utf8))
                    : parseFloat(item.price) || 0;
                const valorAdicionais = calcularValorAdicionais(item.grupos_adicionais_selecionados);

                // Adicionais são POR UNIDADE, multiplicam pela quantidade
                const newTotal = (valorItemBase + valorAdicionais) * novaQuantidade;

                return {
                    ...item,
                    quantity: novaQuantidade,
                    total: newTotal // Número puro
                };
            }
            return item;
        });

        setItensCarrinho(novosItens);
        saveToLocalStorage(novosItens);

        // Sincronizar com backend
        if (syncWithBackend && idEmpresa) {
            try {
                const itemAtualizado = novosItens.find((item, i) => i === index || item.id === itemId);
                if (itemAtualizado) {
                    await updateItemCarrinho(idEmpresa, itemAtualizado.id, itemAtualizado.quantity);
                }
            } catch (error) {
                console.error('[CARRINHO] Erro ao atualizar quantidade no servidor:', error);
            }
        }
    }, [itensCarrinho, idEmpresa, syncWithBackend, calcularValorAdicionais, saveToLocalStorage]);

    /**
     * Decrementa quantidade ou remove item
     */
    const decrementarQuantidade = useCallback(async (itemId, index) => {
        const itemToUpdate = itensCarrinho[index];

        if (itemToUpdate.quantity === 1) {
            // Remover item
            const novosItens = itensCarrinho.filter((item, i) => i !== index && item.id !== itemId);
            setItensCarrinho(novosItens);
            saveToLocalStorage(novosItens);

            // Sincronizar com backend
            if (syncWithBackend && idEmpresa) {
                try {
                    await removeItemCarrinho(idEmpresa, itemToUpdate.id);
                } catch (error) {
                    console.error('[CARRINHO] Erro ao remover item no servidor:', error);
                }
            }
        } else {
            // Decrementar quantidade
            const novosItens = itensCarrinho.map((item, i) => {
                if (i === index || item.id === itemId) {
                    const novaQuantidade = item.quantity - 1;
                    const valorItemBase = typeof item.price === 'string' && item.price.includes('U2F')
                        ? parseFloat(CryptoJS.AES.decrypt(item.price, SECRET_KEY).toString(CryptoJS.enc.Utf8))
                        : parseFloat(item.price) || 0;
                    const valorAdicionais = calcularValorAdicionais(item.grupos_adicionais_selecionados);

                    // Adicionais são POR UNIDADE, multiplicam pela quantidade
                    const newTotal = (valorItemBase + valorAdicionais) * novaQuantidade;

                    return {
                        ...item,
                        quantity: novaQuantidade,
                        total: newTotal // Número puro
                    };
                }
                return item;
            });

            setItensCarrinho(novosItens);
            saveToLocalStorage(novosItens);

            // Sincronizar com backend
            if (syncWithBackend && idEmpresa) {
                try {
                    const itemAtualizado = novosItens.find((item, i) => i === index || item.id === itemId);
                    if (itemAtualizado) {
                        await updateItemCarrinho(idEmpresa, itemAtualizado.id, itemAtualizado.quantity);
                    }
                } catch (error) {
                    console.error('[CARRINHO] Erro ao atualizar quantidade no servidor:', error);
                }
            }
        }
    }, [itensCarrinho, idEmpresa, syncWithBackend, calcularValorAdicionais, saveToLocalStorage]);

    /**
     * Limpa todo o carrinho
     */
    const limparCarrinho = useCallback(async () => {
        setItensCarrinho([]);
        saveToLocalStorage([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY);

        // Sincronizar com backend
        if (syncWithBackend && idEmpresa) {
            try {
                await clearCarrinho(idEmpresa);
            } catch (error) {
                console.error('[CARRINHO] Erro ao limpar carrinho no servidor:', error);
            }
        }
    }, [idEmpresa, syncWithBackend, saveToLocalStorage]);

    /**
     * Finaliza carrinho após criar pedido
     */
    const finalizar = useCallback(async (pedidoId) => {
        // Limpar carrinho local
        setItensCarrinho([]);
        saveToLocalStorage([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY);

        // Sincronizar com backend
        if (syncWithBackend && idEmpresa && pedidoId) {
            try {
                await finalizarCarrinho(idEmpresa, pedidoId);
            } catch (error) {
                console.error('[CARRINHO] Erro ao finalizar carrinho no servidor:', error);
            }
        }
    }, [idEmpresa, syncWithBackend, saveToLocalStorage]);

    // ===== TRACKING DE ABANDONO =====

    /**
     * Atualiza o último passo do usuário
     */
    const atualizarPasso = useCallback(async (passo) => {
        setUltimoPasso(passo);

        if (trackAbandonment && syncWithBackend && idEmpresa && itensCarrinho.length > 0) {
            try {
                await atualizarPassoCarrinho(idEmpresa, passo);
            } catch (error) {
                console.error('[CARRINHO] Erro ao atualizar passo:', error);
            }
        }
    }, [idEmpresa, itensCarrinho.length, syncWithBackend, trackAbandonment]);

    /**
     * Marca carrinho como abandonado
     */
    const marcarAbandonado = useCallback(async (motivo = 'fechou_aba') => {
        if (trackAbandonment && syncWithBackend && idEmpresa && itensCarrinho.length > 0) {
            try {
                await abandonarCarrinho(idEmpresa, motivo, ultimoPasso);
            } catch (error) {
                console.error('[CARRINHO] Erro ao marcar abandono:', error);
            }
        }
    }, [idEmpresa, itensCarrinho.length, ultimoPasso, syncWithBackend, trackAbandonment]);

    // ===== EVENTOS DE ABANDONO =====

    useEffect(() => {
        if (!trackAbandonment || !idEmpresa) return;

        // Evento: usuário fechou aba/navegador
        const handleBeforeUnload = () => {
            if (itensCarrinho.length > 0) {
                // ✅ CORREÇÃO: Usar Blob com Content-Type JSON para sendBeacon funcionar
                const apiUrl = getApiUrl();

                const url = `${apiUrl}/api/v1/carrinho/${idEmpresa}/abandonar`;
                const data = JSON.stringify({
                    motivo: 'fechou_aba',
                    ultimo_passo: ultimoPasso
                });

                // ✅ Usar Blob para garantir Content-Type correto
                const blob = new Blob([data], { type: 'application/json' });
                navigator.sendBeacon(url, blob);

                console.log('[CARRINHO] sendBeacon enviado para abandono');
            }
        };

        // Evento: usuário mudou de aba (visibilidade)
        let visibilityTimeout = null;
        const handleVisibilityChange = () => {
            if (document.hidden && itensCarrinho.length > 0) {
                // ✅ AJUSTE: 15 minutos = 900000ms (igual Uber Eats)
                // Mas aqui só marca após 5 min de aba oculta como precaução
                visibilityTimeout = setTimeout(() => {
                    if (document.hidden) {
                        marcarAbandonado('timeout');
                    }
                }, 5 * 60 * 1000); // 5 minutos escondido
            } else if (!document.hidden && visibilityTimeout) {
                // Usuário voltou, cancelar timeout
                clearTimeout(visibilityTimeout);
                visibilityTimeout = null;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (visibilityTimeout) clearTimeout(visibilityTimeout);
        };
    }, [idEmpresa, itensCarrinho.length, ultimoPasso, trackAbandonment, marcarAbandonado]);

    // ===== RETORNO DO HOOK =====

    return {
        // Estado
        itensCarrinho,
        carrinhoId,
        sessionToken,
        isLoading,
        isSyncing,
        ultimoPasso,
        quantidadeItens: itensCarrinho.length,
        valorTotal: calcularValorTotal(),
        valorTotalFormatado: formatarValor(calcularValorTotal()),
        isEmpty: itensCarrinho.length === 0,

        // Funções de manipulação
        adicionarItem,
        incrementarQuantidade,
        decrementarQuantidade,
        limparCarrinho,
        finalizar,

        // Funções de cálculo
        calcularValorAdicionais,
        calcularSubtotalItem,
        calcularValorTotal,
        formatarValor,

        // Tracking
        atualizarPasso,
        marcarAbandonado,

        // Sync
        syncWithServer,
        saveToLocalStorage,
        loadFromLocalStorage,

        // Setters para compatibilidade
        setItensCarrinho
    };
};

export default useCarrinho;
